import React, { useEffect, useState } from 'react'
import { Link, Redirect } from 'react-router-dom'
import * as yup from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowCircleLeft } from '@fortawesome/pro-regular-svg-icons/faArrowCircleLeft'

import { toast } from 'react-toastify'
import { Helmet } from 'react-helmet'

import { Heading, NotFoundMessage, UnauthorisedMessage } from '@peracto/peracto-ui'
import { useConfig } from '@peracto/peracto-config'

import { GET_ONE, UPDATE, useClient, getSchemaFromResource } from '@peracto/client'

import { MODE_EDIT } from '../CountriesForm'

const CountriesEditContainer = ({ label, children }) => {
    return (
        <div className="form-container">
            <Heading name={`${label} | Edit Country`}>
                <div className="!grow !flex !items-center !justify-end">
                    <Link
                        className="btn btn-outline-primary"
                        data-testid="back-to-countries"
                        to="/countries"
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} className="!mr-2" />
                        Back to Countries
                    </Link>
                </div>
            </Heading>
            {children}
        </div>
    )
}

const CountriesEdit = ({ CountriesForm, match: { params } }) => {
    const { client, getResource } = useClient()
    const [loading, setLoading] = useState(true)
    const [unauthorised, setUnauthorised] = useState(false)
    const [notFound, setNotFound] = useState(false)
    const [redirect, setRedirect] = useState(null)
    const [countryData, setCountryData] = useState()

    const config = useConfig()
    const formatDate = config.get('user_format_date', config.get('format_date'))

    const fetchCountry = async () => {
        try {
            const { data, response } = await client(GET_ONE, 'countries', {
                id: `/countries/${params.country}`,
            })

            if (response.status === 404) {
                setRedirect('/countries')
                setLoading(false)
                return
            }

            const parsedData = {
                ...data,
            }

            setCountryData(parsedData)
            setLoading(false)
        } catch (e) {
            console.error(e)

            if (e.status === 403) {
                setUnauthorised(true)
            }

            if (e.status === 404) {
                setNotFound(true)
            }

            // setRedirect('/redirects')
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCountry()
    }, [])

    const schema = getSchemaFromResource(getResource('countries')).shape({
        storeMode: yup.string(),
        ecommerceEnabled: yup.bool(),
        distributorUrl: yup.string().nullable(),
        localisedName: yup.string().nullable(),
        flagUrl: yup.string().nullable(),
        validForBilling: yup.bool(),
        validForDelivery: yup.bool(),
    })

    const onSubmit = async (data, actions) => {
        try {
            const response = await client(UPDATE, 'countries', {
                id: `/countries/${params.country}`,
                data: data,
            })

            if (response.data.violations && response.data.violations.length > 0) {
                // Display errors for invalid fields
                actions.setSubmitting(false)
                response.data.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            } else {
                actions.setSubmitting(false)
                toast.success('Country successfully updated!')
                setRedirect('/countries')
            }
        } catch (e) {
            console.error(e)
            toast.error(
                e?.error?.body?.hasOwnProperty('hydra:description')
                    ? e.error.body['hydra:description']
                    : 'Whoops, there was a problem...'
            )
            if (e?.error?.body?.violations?.length > 0) {
                // Display errors for invalid fields
                e.error.body.violations.map(error => {
                    actions.setFieldError(error.propertyPath, error.message)
                })
            }
            actions.setSubmitting(false)
        }
    }

    if (loading) {
        return (
            <CountriesEditContainer label={countryData?.label || ''}>
                <div className="card">
                    <div className="card-body">Loading...</div>
                </div>
            </CountriesEditContainer>
        )
    }

    if (unauthorised) {
        return <UnauthorisedMessage />
    }

    if (notFound) {
        return (
            <NotFoundMessage
                url="/countries"
                message="The country you're looking for could not be found"
                buttonLabel="Go to Countries"
            />
        )
    }

    return (
        <CountriesEditContainer label={countryData?.label || ''}>
            {redirect ? (
                <Redirect to={redirect} />
            ) : (
                <>
                    <Helmet>
                        <title>{countryData?.label || 'Country'} | Edit | Peracto</title>
                    </Helmet>
                    <CountriesForm
                        values={countryData}
                        onSubmit={onSubmit}
                        schema={schema}
                        mode={MODE_EDIT}
                    />
                </>
            )}
        </CountriesEditContainer>
    )
}

export default CountriesEdit
